<template>
  <div
    class="row"
    style="height: 90%; width: 200px; line-height: normal; margin: 8px 0px; column-gap: 8px; row-gap: 4px"
  >
    <div
      class="p-0 d-flex"
      :id="'zimmerkontingent-' + zimmerkontingent.zimmertyp + '-' + reiseterminId"
      v-for="(zimmerkontingent, index) in validZimmerkontingente"
      :key="
        isPlan
          ? zimmerkontingent.zimmertyp + '-' + reiseterminId + '-' + index
          : zimmerkontingent.zimmertyp + '-' + reiseterminId + '-' + zimmerkontingent.partner
      "
    >
      <div class="zimmerkontingent-badge" :class="istBgColor(zimmerkontingent)">
        <div class="zimmerkontingent-status">
          {{ statusLabel(zimmerkontingent) }}
        </div>
        <div class="zimmerkontingent-zimmertyp">
          {{ zimmerkontingent.zimmertyp }}
          <span
            v-if="isPlan ? zimmerkontingent.teiltkontingentMit : zimmerkontingent.kontingentAktuellGeteilt"
            :title="isPlan ? null : `Geteiltes Kontingent: ${zimmerkontingent.kontingentAktuellGeteilt}`"
            class="d-inline-flex"
          >
            <Star :size="8" color="#F64E60" fill="#F64E60" />
          </span>
        </div>
      </div>

      <b-tooltip
        v-if="isPlan && zimmerkontingent.teiltkontingentMit"
        placement="bottom"
        :target="'zimmerkontingent-' + zimmerkontingent.zimmertyp + '-' + reiseterminId"
      >
        <span>{{ starTitle(zimmerkontingent) }}</span>
      </b-tooltip>
      <b-tooltip
        custom-class="custom-tooltip-zimmerkontingent-widget"
        :target="'zimmerkontingent-' + zimmerkontingent.zimmertyp + '-' + reiseterminId"
        v-if="!isPlan && isZvc"
        placement="left"
        @show="loadZuschlagHistory(zimmerkontingent.id)"
        @hide="dataLineChart = []"
      >
        <span>Einheiten frei: {{ zimmerkontingent.einheitenFrei }}</span>
        <LineChart
          v-if="dataLineChart.length > 0"
          style="width: 300px; height: 200px; margin: 0; padding: 0"
          :datapoints="dataLineChart"
          :showLegend="false"
        ></LineChart>
      </b-tooltip>
      <b-tooltip
        custom-class="tooltip-zimmerkontingent-verfaelle"
        placement="left"
        :target="'zimmerkontingent-' + zimmerkontingent.zimmertyp + '-' + reiseterminId"
        v-else-if="!isPlan"
      >
        <div>
          <span class="font-weight-bold">Zuschlag {{ zimmerkontingent.zimmertyp }}:</span>
          {{ !isNaN(zimmerkontingent.zuschlag) ? zimmerkontingent.zuschlag + '€' : '' }}
        </div>
        <div v-if="kontingentverfaelle(zimmerkontingent).length > 0" class="mt-1">
          <span class="font-weight-bold">
            {{ kontingentverfaelle(zimmerkontingent).length === 1 ? 'Verfall' : 'Verfälle' }}:
          </span>
          {{
            kontingentverfaelle(zimmerkontingent)
              .map(verfall => formatVerfallsdatum(verfall.verfallsdatum))
              .join(', ')
          }}
        </div>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
import apiService from '@/core/common/services/api.service';
import LineChart from '@/components/produkte/reisetermine/line-chart.vue';
import { Star } from 'lucide-vue';
import { format, isValid } from 'date-fns';

export default {
  props: {
    zimmerkontingente: { type: Array, required: true },
    reiseterminId: { type: Number, required: true },
    isZvc: { type: Boolean },
    // zeigt geplante Zimmerkontingente
    isPlan: { type: Boolean, default: false },
  },
  data() {
    return {
      dataLineChart: [],
    };
  },
  components: {
    LineChart,
    Star,
  },
  computed: {
    validZimmerkontingente() {
      if (this.zimmerkontingente) {
        return this.zimmerkontingente
          .filter(z => this.isPlan || z.isDeleted === false)
          .sort((a, b) => {
            const order = { DZ: 1, EZ: 2 };
            const orderA = order[a.zimmertyp] || 3;
            const orderB = order[b.zimmertyp] || 3;

            if (orderA !== orderB) {
              return orderA - orderB;
            } else {
              return a.zimmertyp.localeCompare(b.zimmertyp);
            }
          });
      } else return [];
    },
  },
  methods: {
    loadZuschlagHistory(id) {
      apiService.get('/history/zimmerkontingente/' + id).then(response => {
        const { result } = response.data;
        const aufpreiseChanges = [];
        for (const key in result) {
          const filteredZuschlag = result[key].changes.filter(item => item.key === 'EinheitenFrei');
          if (filteredZuschlag.length > 0) {
            filteredZuschlag.forEach(item => {
              const laterKeyDate = format(new Date(key), 'dd.MM. HH:mm');
              aufpreiseChanges.push({ date: laterKeyDate, value: item.newValue });
            });
          }
        }
        this.dataLineChart = aufpreiseChanges;
      });
    },
    istBgColor(zimmerkontingent) {
      // Plan-Kontingente
      if (this.isPlan) return 'gray';
      return zimmerkontingent.verfuegbarkeitsStatus == 'Verfuegbar'
        ? 'green'
        : zimmerkontingent.verfuegbarkeitsStatus == 'NichtVerfuegbar'
        ? 'red'
        : 'gray';
    },
    statusLabel(zimmerkontingent) {
      if (this.isPlan) return zimmerkontingent.anzahlEinheiten;
      return zimmerkontingent.verfuegbarkeitsStatus == 'Verfuegbar'
        ? zimmerkontingent.einheitenFrei
        : zimmerkontingent.verfuegbarkeitsStatus == 'NichtVerfuegbar'
        ? 'N'
        : 'RQ';
    },
    // Kontingentverfälle ohne Restkontingent aus dem JSON-Attribut, aufsteigend nach Verfallsdatum sortiert
    kontingentverfaelle(zimmerkontingent) {
      const value = zimmerkontingent.kontingentverfaelle;
      if (!value) return [];
      try {
        const verfaelle = typeof value === 'string' ? JSON.parse(value) : value;
        if (!Array.isArray(verfaelle)) return [];
        return verfaelle
          .filter(v => v.restkontingent === 0)
          .sort((a, b) => new Date(a.verfallsdatum) - new Date(b.verfallsdatum));
      } catch {
        return [];
      }
    },
    formatVerfallsdatum(datum) {
      const date = new Date(datum);
      return isValid(date) ? format(date, 'dd.MM.yyyy') : '-';
    },
    starTitle(zimmerkontingent) {
      return this.isPlan
        ? `Teilt Kontingent mit ${zimmerkontingent.anzahlGeteiltesKontingent} ${zimmerkontingent.teiltkontingentMit}`
        : `Geteiltes Kontingent: ${zimmerkontingent.kontingentAktuellGeteilt}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/mixins/_kachel-badge.scss';

.column {
  flex: 1;
}
.tooltip.show {
  opacity: 1 !important;
}
.tooltip {
  opacity: 1 !important;
}

.zimmerkontingent-badge {
  @include kachel-badge-base;
  flex-direction: column;
  padding: 4px;
  width: 33px;

  @include kachel-badge-colors;
}
.zimmerkontingent-status {
  font-weight: 700;
  font-size: 12px;
  line-height: 12px;
}
.zimmerkontingent-zimmertyp {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  font-weight: 400;
  font-size: 9px;
  line-height: 11px;
}
</style>
<style>
.tooltip-zimmerkontingent-verfaelle.show {
  opacity: 1;
}
.tooltip-zimmerkontingent-verfaelle > .tooltip-inner {
  max-width: 320px;
}
.custom-tooltip-zimmerkontingent-widget > .tooltip-inner {
  max-width: none;
  width: 500;
  font-size: 14px;
}
</style>
